import React, { useState, useEffect, useCallback } from 'react';
import { Button, Collapse } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { useStateSelector } from '../../../../store/selectors';
import createNotification from '../../../../utils/createNotification';
import Helpers from '../../../../utils/helper';
import {
    FaqAttachmentType,
    FaqImagePosition,
    FaqStatus,
    GetFaqItemDto,
} from './models/faq-item.model';
import { FaqItemModal } from './FaqItemModal/FaqItemModal';
import { FaqImage } from './FaqImage';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import VideoPlayerPopup from '../../../../containers/VideoPlayerPopup/VideoPlayerPopup';
import './AdminFaqs.scss';

interface MarketplaceFaqsProps {
    isAdmin: boolean;
    engagementId?: number;
}

const VideoIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="video-icon">
        <path d="M4 4a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-3.17l4 2.29V6.88l-4 2.29V6a2 2 0 00-2-2H4z" />
    </svg>
);

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className={`faq-chevron ${isOpen ? 'open' : ''}`}>
        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
    </svg>
);

export const MarketplaceFaqs = ({
    isAdmin,
    engagementId,
}: MarketplaceFaqsProps) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [faqItems, setFaqItems] = useState<GetFaqItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [openItems, setOpenItems] = useState<Set<string>>(new Set());
    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [editItem, setEditItem] = useState<GetFaqItemDto | null>(null);
    const [isCreateVideoModalVisible, setIsCreateVideoModalVisible] =
        useState(false);
    const [editVideoItem, setEditVideoItem] = useState<GetFaqItemDto | null>(
        null
    );
    const [deleteItem, setDeleteItem] = useState<GetFaqItemDto | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [activeVideo, setActiveVideo] = useState<{
        url: string;
        title: string;
        description?: string;
    } | null>(null);

    const fetchFaqItems = useCallback(async () => {
        if (!axios) return;
        const url = engagementId
            ? `/api/MarketplaceFaq?engagementId=${engagementId}`
            : '/api/MarketplaceFaq';
        const response = await axios.get(url);
        if (!response.data?.isError) {
            const items: GetFaqItemDto[] = response.data?.data || [];
            items.sort((a, b) => {
                if (!a.dateCreated) return -1;
                if (!b.dateCreated) return 1;
                return (
                    new Date(a.dateCreated).getTime() -
                    new Date(b.dateCreated).getTime()
                );
            });
            setFaqItems(items);
        }
    }, [axios, engagementId]);

    const loadFaqItems = useCallback(async () => {
        setIsLoading(true);
        try {
            await fetchFaqItems();
        } catch {
            createNotification(
                'Could not load FAQ items. Please try again.',
                'error'
            );
        } finally {
            setIsLoading(false);
        }
    }, [fetchFaqItems]);

    const refreshFaqItems = useCallback(async () => {
        try {
            await fetchFaqItems();
        } catch {
            createNotification(
                'Could not load FAQ items. Please try again.',
                'error'
            );
        }
    }, [fetchFaqItems]);

    useEffect(() => {
        loadFaqItems();
    }, [loadFaqItems]);

    const toggleItem = (item: GetFaqItemDto) => {
        const id = item.faqItemId;
        setOpenItems((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
                if (
                    item.attachmentType === FaqAttachmentType.Image &&
                    !imageUrls[id]
                ) {
                    axios
                        .get(`/api/MarketplaceFaq/${id}/content`)
                        .then((res) => {
                            if (!res.data?.isError) {
                                setImageUrls((prev) => ({
                                    ...prev,
                                    [id]: res.data?.data,
                                }));
                            }
                        })
                        .catch(() => {});
                }
            }
            return next;
        });
    };

    const handleEditSaved = async (faqItemId: string) => {
        setImageUrls((prev) => {
            const next = { ...prev };
            delete next[faqItemId];
            return next;
        });

        await refreshFaqItems();

        if (openItems.has(faqItemId)) {
            axios
                .get(`/api/MarketplaceFaq/${faqItemId}/content`)
                .then((res) => {
                    if (!res.data?.isError && res.data?.data) {
                        setImageUrls((prev) => ({
                            ...prev,
                            [faqItemId]: res.data?.data,
                        }));
                    }
                })
                .catch(() => {});
        }
    };

    const handleDelete = async () => {
        if (!deleteItem) return;
        setIsDeleting(true);
        try {
            const response = await axios.delete(
                `/api/MarketplaceFaq/admin/${deleteItem.faqItemId}`
            );
            if (!response.data?.isError) {
                const label =
                    deleteItem.attachmentType === FaqAttachmentType.Video
                        ? 'Video'
                        : 'FAQ item';
                createNotification(
                    `${label} has been successfully deleted!`,
                    'success'
                );
                setDeleteItem(null);
                loadFaqItems();
            } else {
                createNotification(
                    response.data.message ?? 'Could not delete.',
                    'error'
                );
            }
        } catch {
            createNotification('Could not delete. Please try again.', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleVideoTileClick = async (video: GetFaqItemDto) => {
        try {
            const res = await axios.get(
                `/api/MarketplaceFaq/${video.faqItemId}/content`
            );
            if (!res.data?.isError) {
                setActiveVideo({
                    url: res.data?.data,
                    title: video.question,
                    description: video.answer || undefined,
                });
            } else {
                createNotification(
                    'Could not load video. Please try again.',
                    'error'
                );
            }
        } catch {
            createNotification(
                'Could not load video. Please try again.',
                'error'
            );
        }
    };

    const faqQuestions = faqItems.filter(
        (item) => item.attachmentType !== FaqAttachmentType.Video
    );
    const explanatoryVideos = faqItems.filter(
        (item) => item.attachmentType === FaqAttachmentType.Video
    );

    const isVideoBeingDeleted =
        deleteItem?.attachmentType === FaqAttachmentType.Video;

    if (isLoading) {
        return (
            <div className="marketplace-faqs">
                {Helpers.renderTableLoader()}
            </div>
        );
    }

    return (
        <div className="marketplace-faqs">
            <div className="marketplace-faqs-section">
                <div className="section-header">
                    <span className="section-title">FAQs</span>
                    {isAdmin && (
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => setIsCreateModalVisible(true)}>
                            Create New
                        </Button>
                    )}
                </div>
                <div className="faq-items">
                    {faqQuestions.length === 0 ? (
                        <span className="no-data-label">
                            No FAQ items available
                        </span>
                    ) : (
                        faqQuestions.map((item) => {
                            const isOpen = openItems.has(item.faqItemId);
                            return (
                                <div key={item.faqItemId} className="faq-item">
                                    <div
                                        className={`faq-item-header ${isOpen ? 'open' : ''}${item.status === FaqStatus.Draft ? ' draft' : ''}`}
                                        onClick={() => toggleItem(item)}>
                                        <span
                                            className={`faq-question${item.status === FaqStatus.Draft ? ' faq-question--draft' : ''}`}>
                                            {item.question}
                                            {item.status === FaqStatus.Draft
                                                ? ' (Draft)'
                                                : ''}
                                        </span>
                                        <div className="faq-header-actions">
                                            {isAdmin && (
                                                <>
                                                    <button
                                                        className="faq-action-btn"
                                                        title="Edit"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditItem(item);
                                                        }}>
                                                        <FontAwesomeIcon
                                                            icon={faEdit}
                                                        />
                                                    </button>
                                                    <button
                                                        className="faq-action-btn faq-action-btn--delete"
                                                        title="Delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteItem(item);
                                                        }}>
                                                        <FontAwesomeIcon
                                                            icon={faTrashCan}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                            <ChevronIcon isOpen={isOpen} />
                                        </div>
                                    </div>
                                    <Collapse isOpen={isOpen}>
                                        <div
                                            className={`faq-item-answer faq-item-answer--${item.attachmentType === FaqAttachmentType.Image && imageUrls[item.faqItemId] ? FaqImagePosition[item.imagePosition ?? FaqImagePosition.Right].toLowerCase() : 'no-image'}`}>
                                            {item.attachmentType ===
                                                FaqAttachmentType.Image &&
                                                imageUrls[item.faqItemId] && (
                                                    <FaqImage
                                                        src={
                                                            imageUrls[
                                                                item.faqItemId
                                                            ]
                                                        }
                                                    />
                                                )}
                                            <span>{item.answer}</span>
                                        </div>
                                    </Collapse>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <div className="marketplace-faqs-section">
                <div className="section-header">
                    <span className="section-title">Explanatory Videos</span>
                    {isAdmin && (
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => setIsCreateVideoModalVisible(true)}>
                            Create New
                        </Button>
                    )}
                </div>
                <div className={`video-grid${explanatoryVideos.length <= 3 ? ' video-grid--few' : ''}`}>
                    {explanatoryVideos.length === 0 ? (
                        <span className="no-data-label">
                            No videos available
                        </span>
                    ) : (
                        explanatoryVideos.map((video) => (
                            <div
                                key={video.faqItemId}
                                className={`video-tile${video.status === FaqStatus.Draft ? ' video-tile--draft' : ''}`}
                                onClick={() => handleVideoTileClick(video)}>
                                <div className="video-icon-wrapper">
                                    <VideoIcon />
                                </div>
                                <span
                                    className={`video-name${video.status === FaqStatus.Draft ? ' video-name--draft' : ''}`}>
                                    {video.question}
                                    {video.status === FaqStatus.Draft
                                        ? ' (Draft)'
                                        : ''}
                                </span>
                                {isAdmin && (
                                    <div className="video-tile-actions">
                                        <button
                                            className="video-tile-action-btn"
                                            title="Edit"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEditVideoItem(video);
                                            }}>
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            className="video-tile-action-btn video-tile-action-btn--delete"
                                            title="Delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteItem(video);
                                            }}>
                                            <FontAwesomeIcon
                                                icon={faTrashCan}
                                            />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {isCreateModalVisible && (
                <FaqItemModal
                    isVisible={isCreateModalVisible}
                    onClose={() => setIsCreateModalVisible(false)}
                    onSaved={loadFaqItems}
                />
            )}

            {editItem &&
                (() => {
                    const id = editItem.faqItemId;
                    return (
                        <FaqItemModal
                            isVisible={!!editItem}
                            onClose={() => setEditItem(null)}
                            onSaved={() => handleEditSaved(id)}
                            item={editItem}
                        />
                    );
                })()}

            {isCreateVideoModalVisible && (
                <FaqItemModal
                    isVisible={isCreateVideoModalVisible}
                    onClose={() => setIsCreateVideoModalVisible(false)}
                    onSaved={loadFaqItems}
                    mode="video"
                />
            )}

            {editVideoItem &&
                (() => {
                    const id = editVideoItem.faqItemId;
                    return (
                        <FaqItemModal
                            isVisible={!!editVideoItem}
                            onClose={() => setEditVideoItem(null)}
                            onSaved={() => handleEditSaved(id)}
                            item={editVideoItem}
                            mode="video"
                        />
                    );
                })()}

            <ConfirmationModal
                isVisible={!!deleteItem}
                setIsVisible={(v: boolean) => {
                    if (!v) setDeleteItem(null);
                }}
                header={
                    isVideoBeingDeleted ? 'Delete Video' : 'Delete FAQ Item'
                }
                confirmationText={`Are you sure you want to delete this ${isVideoBeingDeleted ? 'video' : 'FAQ item'} permanently?`}
                nextButtonText="Delete"
                nextButtonLoaderText="Deleting"
                cancelButtonText="Cancel"
                isLoading={isDeleting}
                closeOnConfirm={false}
                onConfirm={handleDelete}
            />

            {activeVideo && (
                <VideoPlayerPopup
                    title={activeVideo.title}
                    src={activeVideo.url}
                    onClose={() => setActiveVideo(null)}
                    isRecordingEnabled={false}
                    hideContextMenu={true}
                    description={activeVideo.description}
                />
            )}
        </div>
    );
};
